'use client'

import { BodyText, LeftCaretIcon } from 'design-configuration/components'
import { Button } from '@components/button'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

const dropdownVariants = cva('absolute right-0 top-full mt-xs z-50 flex flex-col rounded-md border p-xs', {
  variants: {
    colorTheme: {
      outline: 'bg-surface text-on-surface border-outline-variant w-fit',
      'outline-inverted': 'bg-surface-inverted text-on-surface-inverted border-outline-inverted w-full'
    }
  },
  defaultVariants: {
    colorTheme: 'outline'
  }
})

const optionVariants = cva('p-xs w-full rounded', {
  variants: {
    colorTheme: {
      outline: 'transition-colors duration-200 selected:bg-surface-container hover:bg-surface-container',
      'outline-inverted': 'transition-colors duration-200 selected:bg-surface-container-inverted'
    }
  },
  defaultVariants: {
    colorTheme: 'outline'
  }
})

const iconVariants = cva('w-sm h-sm transition-transform duration-200', {
  variants: {
    colorTheme: {
      outline: 'fill-on-surface',
      'outline-inverted': 'fill-on-surface-inverted-variant'
    }
  },
  defaultVariants: {
    colorTheme: 'outline'
  }
})

export const Dropdown = ({
  options,
  selected,
  setSelected,
  title,
  className,
  colorTheme = 'outline'
}: {
  options: string[]
  selected: string
  setSelected: (option: string) => void
  title?: string
  className?: string
  colorTheme?: 'outline' | 'outline-inverted'
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOptionClick = (option: string) => {
    setSelected(option)
    setIsOpen(false)
  }

  const ref = useRef<HTMLDivElement>(null)

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return (
    <div className={cn('relative', className)} ref={ref}>
      <Button
        className="gap-xs w-full justify-between"
        colorTheme={colorTheme}
        onClick={() => setIsOpen(!isOpen)}
        iconRight={
          <LeftCaretIcon
            className={cn(iconVariants({ colorTheme }), {
              'rotate-90': isOpen,
              '-rotate-90': !isOpen
            })}
          />
        }
      >
        <BodyText className="sm:hidden">{'Sort'}</BodyText>
        <BodyText className="hidden sm:block">{title ?? selected ?? 'Select'}</BodyText>
      </Button>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            key="dropdown"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={dropdownVariants({ colorTheme })}
          >
            {options.map((option) => (
              <motion.button
                key={option}
                onClick={() => handleOptionClick(option)}
                className={cn(optionVariants({ colorTheme }), {
                  selected: selected === option
                })}
              >
                <BodyText className="whitespace-nowrap text-left">{option}</BodyText>
              </motion.button>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
